import React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { findIndex } from 'lodash';
import RomanNumeral from 'roman-numeral-converter-mmxvi';
import { AppUI } from '@seedlang/state';
import { Constants, Theme } from '@seedlang/constants';
import { kFormatter } from '@seedlang/utils';
import { CommunityBadgeIcon, PluralIcon, BrainIcon, TriviamasterIcon, DerDieDasIcon, MagnifyWordsIcon, BookIcon, ShakespeareIcon, MicrophoneBadgeIcon, CardsIcon, ClockfaceIcon } from '@seedlang/icons';
import Badge from 'components/badge';
import ErrorBoundary from 'components/hoc/error_boundary';
import Modal from 'components/modal';
import SegmentedTriangle from 'components/svg/segmented_triangle';
import Text from 'components/text';

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px;
`;

const BadgeWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 25px;
`;

const TitleWrapper = styled.div`
`;

const TriangleWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
  svg {
    width: 240px;
  }
`;

const DetailWrapper = styled.div`
  width: 100%;
  padding: 18px;
`;

const BadgeLevelsWrapper = styled.div`
  width: 100%;
  margin-top: 20px;
`;

const BadgeLevels = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BadgeLevel = styled.div`
  width: ${props => props.width}px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ICONS = {
  storyteller: ShakespeareIcon,
  interviewMe: MicrophoneBadgeIcon,
  deckmaster: CardsIcon,
  backInTime: ClockfaceIcon,
  storymaster: BookIcon,
  gendermaster: DerDieDasIcon,
  vocabmaster: MagnifyWordsIcon,
  brainiac: BrainIcon,
  triviamaster: TriviamasterIcon,
  pluralmaster: PluralIcon,
  community: CommunityBadgeIcon,
};

@observer
class BadgeShow extends React.Component {
  get currentLevel() {
    const currentLevel = findIndex(
      this.props.badge.levels,
      level => this.props.badge.current < level,
    );
    return currentLevel === -1 ? this.props.badge.levels.length : currentLevel;
  }

  get currentGoal() {
    return this.props.badge.levels[this.currentLevel] || this.props.badge.levels[this.props.badge.levels.length - 1];
  }

  get progressCurrent() {
    return this.props.badge.current;
  }

  get progressSegmentCount() {
    return !this.currentGoal || this.currentGoal <= 0 ? 10 : Math.ceil((this.progressCurrent / this.currentGoal) * 10);
  }

  get levelText() {
    return this.currentLevel > 0 ? `Level ${RomanNumeral.getRomanFromInteger(this.currentLevel)}` : 'Level 0';
  }

  get progressText() {
    const interpolationString = this.props.badge.interpolationString && !this.badgeBelongsToAuthUser && this.badgeUserName
      ? this.props.badge.interpolationString.replace(/^You've /, `${this.badgeUserName} has `)
      : this.props.badge.interpolationString;
    return interpolationString ? interpolationString.replace('%{count}', kFormatter(this.props.badge.current)) : `${this.props.badge.description}: ${kFormatter(this.props.badge.current)}`;
  }

  get badgeBelongsToAuthUser() {
    return !this.props.badge.user || this.props.badge.user.id?.toString() === AppUI.user.id?.toString();
  }

  get badgeUserName() {
    return this.props.badge.user?.name || this.props.badge.user?.firstName;
  }

  get visibleBadgeLevelsWidth() {
    const maxWidth = AppUI.layout.isMobile ? 320 : 430;
    const viewportWidth = AppUI.layout.viewportWidth || maxWidth + 80;
    return Math.max(Math.min(maxWidth, viewportWidth - 80), 0);
  }

  get modalTop() {
    return AppUI.layout.isMobile ? '20px' : '50px';
  }

  get modalMaxHeight() {
    return AppUI.layout.isMobile ? 'calc(100vh - 80px)' : 'calc(100vh - 140px)';
  }

  get badgeLevelWidth() {
    return this.visibleBadgeLevelsWidth / 5;
  }

  get visibleBadgeLevelsCount() {
    return Math.min(this.props.badge.levels.length, 5);
  }

  get badgeLevelsStartIndex() {
    const currentLevelIndex = Math.max(this.currentLevel - 1, 0);
    const maxStartIndex = Math.max(this.props.badge.levels.length - this.visibleBadgeLevelsCount, 0);
    const centeredStartIndex = currentLevelIndex - Math.floor(this.visibleBadgeLevelsCount / 2);
    return Math.min(Math.max(centeredStartIndex, 0), maxStartIndex);
  }

  get visibleBadgeLevels() {
    return this.props.badge.levels
      .slice(this.badgeLevelsStartIndex, this.badgeLevelsStartIndex + this.visibleBadgeLevelsCount)
      .map((levelGoal, index) => {
        return {
          level: this.badgeLevelsStartIndex + index + 1,
          levelGoal,
        };
      });
  }

  get icon() {
    const Icon = ICONS[this.props.badge.badgeSlug];
    return Icon ? <Icon /> : null;
  }

  badgeLevelColor(level) {
    return level <= this.currentLevel ? Constants.BADGE_COLORS[level] : '#e0e0e0';
  }

  render() {
    return (
      <Modal
        width={AppUI.layout.isMobile ? 'calc(100% - 20px)' : '560px'}
        maxWidth='calc(100% - 20px)'
        top={this.modalTop}
        maxHeight={this.modalMaxHeight}
        contentShouldScroll
        padding='20px'
        iconFill='#333'
        iconTop='20px'
        onCloseModal={this.props.onCloseModal}
      >
        <Wrapper>
          <BadgeWrapper>
            <Badge
              hideInfo
              disableTooltip
              alignment='vertical'
              width='160px'
              wrapperWidth='160px'
              wrapperHeight='160px'
              bottom='7px'
              right='7px'
              levelIndicatorWidth='40px'
              levelIndicatorBorderWidth='5px'
              levelIndicatorFontSize='18px'
              name={this.props.badge.name}
              description={this.props.badge.description}
              current={this.props.badge.current}
              levels={this.props.badge.levels}
              icon={this.icon}
            />
          </BadgeWrapper>
          <TitleWrapper>
            <Text
              bold
              heading={1}
              center
              margin='0 0 8px 0'
            >
              {this.props.badge.name}
            </Text>
            <Text
              center
            >
              {this.levelText}
            </Text>
          </TitleWrapper>
          <TriangleWrapper>
            <SegmentedTriangle
              fill1={this.progressSegmentCount >= 1 ? Theme.green : '#e0e0e0'}
              fill2={this.progressSegmentCount >= 2 ? Theme.green : '#e0e0e0'}
              fill3={this.progressSegmentCount >= 3 ? Theme.green : '#e0e0e0'}
              fill4={this.progressSegmentCount >= 4 ? Theme.green : '#e0e0e0'}
              fill5={this.progressSegmentCount >= 5 ? Theme.green : '#e0e0e0'}
              fill6={this.progressSegmentCount >= 6 ? Theme.green : '#e0e0e0'}
              fill7={this.progressSegmentCount >= 7 ? Theme.green : '#e0e0e0'}
              fill8={this.progressSegmentCount >= 8 ? Theme.green : '#e0e0e0'}
              fill9={this.progressSegmentCount >= 9 ? Theme.green : '#e0e0e0'}
              fill10={this.progressSegmentCount >= 10 ? Theme.green : '#e0e0e0'}
            />
          </TriangleWrapper>
          <DetailWrapper>
            <Text
              center
              margin='0 0 8px 0'
            >
              {this.progressText}
            </Text>
            <Text
              center
            >
              {`Next level: ${kFormatter(this.currentGoal)}`}
            </Text>
          </DetailWrapper>
          <BadgeLevelsWrapper>
            <BadgeLevels>
              {
                this.visibleBadgeLevels.map((badgeLevel) => {
                  return (
                    <BadgeLevel
                      key={badgeLevel.levelGoal}
                      width={this.badgeLevelWidth}
                    >
                      <Badge
                        hideInfo
                        disableTooltip
                        alignment='vertical'
                        width='54px'
                        wrapperWidth={`${this.badgeLevelWidth}px`}
                        wrapperHeight='80px'
                        bottom='-6px'
                        right='-6px'
                        levelIndicatorWidth='22px'
                        levelIndicatorBorderWidth='3px'
                        levelIndicatorFontSize='8px'
                        name={this.props.badge.name}
                        description={this.props.badge.description}
                        current={badgeLevel.levelGoal}
                        levels={this.props.badge.levels}
                        icon={this.icon}
                        badgeColorOverride={this.badgeLevelColor(badgeLevel.level)}
                        currentLevelOverride={badgeLevel.level}
                        levelRomanOverride={RomanNumeral.getRomanFromInteger(badgeLevel.level)}
                      />
                      <Text
                        center
                        fontSize='13px'
                        margin='-8px 0 0 0'
                        color={badgeLevel.level <= this.currentLevel ? '#000' : '#777'}
                      >
                        {kFormatter(badgeLevel.levelGoal)}
                      </Text>
                    </BadgeLevel>
                  );
                })
              }
            </BadgeLevels>
          </BadgeLevelsWrapper>
        </Wrapper>
      </Modal>
    );
  }
}

export default ErrorBoundary(BadgeShow);
